// frontend/src/pages/MovieDetailPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, Star, Play, Bookmark, MessageSquare,
  ChevronLeft, Loader, ThumbsUp, Trash2, Edit2,
  AlertTriangle, Send, Film, BookOpen, Sparkles
} from 'lucide-react';
import { useAppContext } from '../context/AppContext';
import { moviesAPI, reviewsAPI, userAPI } from '../services/api';
import { addNotification } from '../utils/notifications';

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────
interface MovieDetail {
  id: number;
  title: string;
  overview?: string;
  posterUrl?: string;
  backdropUrl?: string;
  releaseYear?: number;
  voteAverage?: number;
  voteCount?: number;
  runtime?: number;
  season?: string;
  holiday?: string;
  moods?: string[];
  tropes?: string[];
  trailerUrl?: string;
  originalLanguage?: string;
  tagline?: string;
  favoritesCount?: number;
  reviewsCount?: number;
}

interface Comment {
  id: string;
  content: string;
  likesCount: number;
  createdAt: string;
  user: { id: string; username: string; name: string; avatar?: string };
}

interface Review {
  id: string;
  content: string;
  rating: number;
  containsSpoiler: boolean;
  likesCount: number;
  commentsCount: number;
  createdAt: string;
  user: { id: string; username: string; name: string; avatar?: string };
  comments: Comment[];
  likes: { userId: string }[];
}

// ─────────────────────────────────────────────
// STAR RATING COMPONENT
// ─────────────────────────────────────────────
const StarRating: React.FC<{
  rating: number;
  onRate?: (rating: number) => void;
  size?: 'sm' | 'lg';
}> = ({ rating, onRate, size = 'sm' }) => {
  const [hovered, setHovered] = useState(0);
  const starSize = size === 'lg' ? 'w-8 h-8' : 'w-4 h-4';

  return (
    <div className="flex">
      {[1, 2, 3, 4, 5].map(star => (
        <button
          key={star}
          onClick={() => onRate?.(star)}
          onMouseEnter={() => onRate && setHovered(star)}
          onMouseLeave={() => onRate && setHovered(0)}
          className={onRate ? 'cursor-pointer' : 'cursor-default'}
          disabled={!onRate}
        >
          <Star
            className={`${starSize} transition-colors ${
              star <= (hovered || rating)
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────────
// SHIP RATING COMPONENT
// ─────────────────────────────────────────────
const ShipRating: React.FC<{
  movieTitle: string;
  userRating: number;
  onRate: (rating: number) => void;
  communityAvg?: number;
  totalRatings?: number;
}> = ({ movieTitle, userRating, onRate, communityAvg = 0, totalRatings = 0 }) => {
  const [hovered, setHovered] = useState(0);

  const shipLabels: Record<number, string> = {
    1: 'They should just be friends 😬',
    2: 'Cute but needs work 🌱',
    3: 'Pretty solid ship ⚓',
    4: 'I\'m obsessed with them 💘',
    5: 'My forever OTP 💒',
  };

  const heartColors = [
    'text-gray-400',
    'text-pink-300',
    'text-pink-400',
    'text-red-400',
    'text-red-500',
    'text-romcom-red',
  ];

  const active = hovered || userRating;

  return (
    <div className="bg-gradient-to-br from-romcom-pink/10 via-[#faf8f6] to-romcom-red/5 rounded-2xl border border-romcom-pink/20 p-5 mb-6">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-romcom-red" />
        <h4 className="font-bold text-gray-800 text-sm">Rate the Ship</h4>
        {communityAvg > 0 && (
          <span className="ml-auto text-xs text-gray-400">
            Community avg: <span className="font-semibold text-romcom-red">{communityAvg.toFixed(1)}</span>
            <span className="ml-1 text-gray-300">({totalRatings} ratings)</span>
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 mb-3">
        {[1, 2, 3, 4, 5].map(n => (
          <button
            key={n}
            onClick={() => onRate(n)}
            onMouseEnter={() => setHovered(n)}
            onMouseLeave={() => setHovered(0)}
            className="transition-transform hover:scale-125 active:scale-95"
          >
            <Heart
              className={`w-8 h-8 transition-all duration-150 ${
                n <= active
                  ? `${heartColors[active]} fill-current drop-shadow-sm`
                  : 'text-gray-200'
              }`}
            />
          </button>
        ))}
        {userRating > 0 && (
          <motion.span
            key={userRating}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            className="ml-2 text-xs font-semibold text-romcom-red bg-romcom-pink/20 px-2 py-1 rounded-full"
          >
            {userRating}/5
          </motion.span>
        )}
      </div>

      <AnimatePresence mode="wait">
        {active > 0 && (
          <motion.p
            key={active}
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="text-xs text-romcom-red/80 italic"
          >
            {shipLabels[active]}
          </motion.p>
        )}
        {active === 0 && (
          <motion.p
            key="prompt"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-xs text-gray-400 italic"
          >
            How strong is the chemistry in <span className="font-medium">{movieTitle}</span>?
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─────────────────────────────────────────────
// USER HOVER CARD COMPONENT
// ─────────────────────────────────────────────
const UserHoverCard: React.FC<{
  user: { id: string; username: string; name: string; avatar?: string };
  children: React.ReactNode;
  navigate: (path: string) => void;
}> = ({ user, children, navigate }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const hoverTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseEnter = () => {
    hoverTimeout.current = setTimeout(async () => {
      setIsHovered(true);
      if (!profile) {
        try {
          const res = await userAPI.getPublicProfile(user.username);
          setProfile(res.data.data);
        } catch {
          // Ignore errors for hover card
        }
      }
    }, 400);
  };

  const handleMouseLeave = () => {
    if (hoverTimeout.current) clearTimeout(hoverTimeout.current);
    setIsHovered(false);
  };

  return (
    <div className="relative inline-block" onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {children}
      <AnimatePresence>
        {isHovered && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 top-full mt-2 z-50 w-6 bg-[#faf8f6] rounded-2xl shadow-xl border border-gray-100 overflow-hidden"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={handleMouseLeave}
          >
            <div className="bg-gradient-to-r from-romcom-pink/30 to-romcom-blue/20 px-4 pt-4 pb-8" />
            <div className="px-4 -mt-6 mb-3 flex items-end justify-between">
              <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0" style={{ border: '3px solid white' }}>
                {user.avatar ? (
                  <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-romcom-pink to-romcom-red flex items-center justify-center text-white font-bold text-lg">
                    {(user.name || user.username).charAt(0).toUpperCase()}
                  </div>
                )}
              </div>
              <button
                onClick={() => navigate(`/users/${user.username}`)}
                className="text-xs bg-romcom-red text-white px-3 py-1.5 rounded-full font-medium hover:bg-red-600 transition-colors mb-1"
              >
                View Profile
              </button>
            </div>
            <div className="px-4 pb-4">
              <p className="font-bold text-gray-800">{user.name || user.username}</p>
              <p className="text-xs text-gray-400 mb-3">@{user.username}</p>
              {profile ? (
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="bg-gray-50 rounded-lg py-2">
                    <div className="font-bold text-gray-800 text-sm">{profile.favoritesCount || 0}</div>
                    <div className="text-xs text-gray-400">Favorites</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg py-2">
                    <div className="font-bold text-gray-800 text-sm">{profile.reviewsCount || 0}</div>
                    <div className="text-xs text-gray-400">Reviews</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg py-2">
                    <div className="font-bold text-gray-800 text-sm">{profile.followersCount || 0}</div>
                    <div className="text-xs text-gray-400">Followers</div>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center py-2">
                  <div className="w-4 h-4 border-2 border-romcom-red border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};


// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
const MovieDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state, toggleLike, toggleWatchlist, toggleWatched, openLoginModal } = useAppContext();

  const movieId = parseInt(id || '0');

  // Movie state
  const [movie, setMovie] = useState<MovieDetail | null>(null);
  const [similarMovies, setSimilarMovies] = useState<MovieDetail[]>([]);
  const [isLoadingMovie, setIsLoadingMovie] = useState(true);

  // Reviews state
  const [reviews, setReviews] = useState<Review[]>([]);
  const [isLoadingReviews, setIsLoadingReviews] = useState(true);
  const [reviewsTotal, setReviewsTotal] = useState(0);

  // Review form state
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewContent, setReviewContent] = useState('');
  const [reviewRating, setReviewRating] = useState(5);
  const [containsSpoiler, setContainsSpoiler] = useState(false);
  const [isSubmittingReview, setIsSubmittingReview] = useState(false);
  const [editingReviewId, setEditingReviewId] = useState<string | null>(null);
  const [reviewError, setReviewError] = useState('');

  // Ship rating state
  const [shipRating, setShipRating] = useState(0);
  const [communityShipAvg, setCommunityShipAvg] = useState(0);
  const [totalShipRatings, setTotalShipRatings] = useState(0);

  // Comment state
  const [commentInputs, setCommentInputs] = useState<Record<string, string>>({});
  const [expandedComments, setExpandedComments] = useState<Record<string, boolean>>({});
  const [submittingComment, setSubmittingComment] = useState<string | null>(null);
  const [likedReviews, setLikedReviews] = useState<Set<string>>(new Set());
  const [notebookNote, setNotebookNote] = useState('');
  const [notebookWatchedOn, setNotebookWatchedOn] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [notebookSaved, setNotebookSaved] = useState(false);
  const [notebookError, setNotebookError] = useState('');
  const [isLoggingNotebook, setIsLoggingNotebook] = useState(false);

  const isLiked = state.likedMovies.includes(movieId);
  const isWatchlisted = state.watchlist.includes(movieId);
  const isWatched = state.watchedMovies.includes(movieId);

  // ── Fetch movie ──
  useEffect(() => {
    const fetchMovie = async () => {
      try {
        setIsLoadingMovie(true);
        const response = await moviesAPI.getMovie(movieId);
        const { movie: movieData, similar } = response.data.data;
        setMovie(movieData);
        setSimilarMovies(similar || []);
      } catch (err) {
        console.error('Failed to fetch movie:', err);
      } finally {
        setIsLoadingMovie(false);
      }
    };
    if (movieId) fetchMovie();
  }, [movieId]);

  // ── Fetch reviews ──
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setIsLoadingReviews(true);
        const response = await reviewsAPI.getMovieReviews(movieId);
        const { reviews: reviewData, pagination } = response.data.data;
        setReviews(reviewData);
        setReviewsTotal(pagination.total);

        // Derive community ship avg from review ratings as a proxy
        if (reviewData.length > 0) {
          const avg = reviewData.reduce((s: number, r: Review) => s + r.rating, 0) / reviewData.length;
          setCommunityShipAvg(avg);
          setTotalShipRatings(reviewData.length);
        }
      } catch (err) {
        console.error('Failed to fetch reviews:', err);
      } finally {
        setIsLoadingReviews(false);
      }
    };
    if (movieId) fetchReviews();
  }, [movieId]);

  // ── Handle ship rating ──
  const handleShipRate = (rating: number) => {
    if (!state.currentUser) { openLoginModal(); return; }
    setShipRating(rating);
    // Optimistically update community avg
    const newTotal = totalShipRatings + (shipRating === 0 ? 1 : 0);
    const newAvg = shipRating === 0
      ? (communityShipAvg * totalShipRatings + rating) / newTotal
      : (communityShipAvg * totalShipRatings - shipRating + rating) / totalShipRatings;
    setCommunityShipAvg(newAvg);
    if (shipRating === 0) setTotalShipRatings(newTotal);
  };

  // ── Submit review ──
  const handleSubmitReview = async () => {
    if (!state.currentUser) { openLoginModal(); return; }

    try {
      setIsSubmittingReview(true);
      setReviewError('');

      if (editingReviewId) {
        const response = await reviewsAPI.updateReview(editingReviewId, {
          rating: reviewRating,
          content: reviewContent,
        });
        setReviews(prev => prev.map(r =>
          r.id === editingReviewId ? { ...r, ...response.data.data } : r
        ));
        setEditingReviewId(null);

        // Notification: review updated
        addNotification({
          type: 'review_updated',
          message: `You updated your review for "${movie?.title}"`,
          movieId,
          movieTitle: movie?.title,
        });
      } else {
        const response = await reviewsAPI.createReview(movieId, {
          rating: reviewRating,
          content: reviewContent,
          containsSpoiler,
        });
        setReviews(prev => [response.data.data, ...prev]);
        setReviewsTotal(prev => prev + 1);

        // Notification: review posted
        addNotification({
          type: 'review_posted',
          message: `You reviewed "${movie?.title}" — ${reviewRating}/5 stars ⭐`,
          movieId,
          movieTitle: movie?.title,
        });
      }

      setReviewContent('');
      setReviewRating(5);
      setContainsSpoiler(false);
      setShowReviewForm(false);
    } catch (err: any) {
      setReviewError(err.response?.data?.message || 'Failed to submit review.');
    } finally {
      setIsSubmittingReview(false);
    }
  };

  // ── Delete review ──
  const handleDeleteReview = async (reviewId: string) => {
    if (!window.confirm('Delete your review?')) return;
    try {
      await reviewsAPI.deleteReview(reviewId);
      setReviews(prev => prev.filter(r => r.id !== reviewId));
      setReviewsTotal(prev => prev - 1);
    } catch (err) {
      console.error('Failed to delete review:', err);
    }
  };

  // ── Edit review ──
  const handleEditReview = (review: Review) => {
    setEditingReviewId(review.id);
    setReviewContent(review.content);
    setReviewRating(review.rating);
    setContainsSpoiler(review.containsSpoiler);
    setShowReviewForm(true);
    window.scrollTo({ top: document.getElementById('review-form')?.offsetTop, behavior: 'smooth' });
  };

  // ── Like review ──
  const handleLikeReview = async (reviewId: string) => {
    if (!state.currentUser) { openLoginModal(); return; }
    try {
      await reviewsAPI.likeReview(reviewId);
      setLikedReviews(prev => {
        const next = new Set(prev);
        if (next.has(reviewId)) {
          next.delete(reviewId);
          setReviews(r => r.map(rev => rev.id === reviewId
            ? { ...rev, likesCount: rev.likesCount - 1 } : rev));
        } else {
          next.add(reviewId);
          setReviews(r => r.map(rev => rev.id === reviewId
            ? { ...rev, likesCount: rev.likesCount + 1 } : rev));
        }
        return next;
      });
    } catch (err) {
      console.error('Failed to like review:', err);
    }
  };

  // ── Submit comment ──
  const handleSubmitComment = async (reviewId: string) => {
    if (!state.currentUser) { openLoginModal(); return; }
    const content = commentInputs[reviewId]?.trim();
    if (!content) return;

    try {
      setSubmittingComment(reviewId);
      const response = await reviewsAPI.addComment(reviewId, content);
      setReviews(prev => prev.map(r =>
        r.id === reviewId
          ? { ...r, comments: [...r.comments, response.data.data], commentsCount: r.commentsCount + 1 }
          : r
      ));
      setCommentInputs(prev => ({ ...prev, [reviewId]: '' }));

      // Notification: comment posted
      addNotification({
        type: 'comment_posted',
        message: `You commented on a review for "${movie?.title}"`,
        movieId,
        movieTitle: movie?.title,
      });
    } catch (err) {
      console.error('Failed to add comment:', err);
    } finally {
      setSubmittingComment(null);
    }
  };

  // ── Delete comment ──
  const handleDeleteComment = async (reviewId: string, commentId: string) => {
    try {
      await reviewsAPI.deleteComment(commentId);
      setReviews(prev => prev.map(r =>
        r.id === reviewId
          ? { ...r, comments: r.comments.filter(c => c.id !== commentId), commentsCount: r.commentsCount - 1 }
          : r
      ));
    } catch (err) {
      console.error('Failed to delete comment:', err);
    }
  };

  const userReview = state.currentUser?.id
    ? reviews.find(r => r.user.id === String(state.currentUser!.id))
    : undefined;
  const rating = movie?.voteAverage ? (movie.voteAverage / 2) : 0;

  const handleLogNotebook = async () => {
    if (!state.currentUser) {
      openLoginModal();
      return;
    }
    if (!notebookWatchedOn || !/^\d{4}-\d{2}-\d{2}$/.test(notebookWatchedOn)) {
      setNotebookError('Pick the date you watched this movie.');
      return;
    }
    setNotebookError('');
    setNotebookSaved(false);
    try {
      setIsLoggingNotebook(true);
      const note = notebookNote.trim();
      await moviesAPI.logNotebook(movieId, {
        watchedOn: notebookWatchedOn,
        ...(note ? { note } : {}),
      });
      setNotebookNote('');
      setNotebookSaved(true);

      // Notification: notebook entry added
      addNotification({
        type: 'notebook_logged',
        message: `You logged "${movie?.title}" in The Notebook 📖`,
        movieId,
        movieTitle: movie?.title,
      });

      window.setTimeout(() => setNotebookSaved(false), 4000);
    } catch (err: any) {
      console.error('Failed to log to notebook:', err);
      setNotebookError(
        err.response?.data?.message || 'Could not save to your notebook.'
      );
    } finally {
      setIsLoggingNotebook(false);
    }
  };

  // ── Loading ──
  if (isLoadingMovie) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 text-romcom-red animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Loading movie details...</p>
        </div>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🎬</div>
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Movie not found</h2>
          <button onClick={() => navigate('/movies')} className="btn-primary mt-4">
            Back to Movies
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-romcom-pink/10 to-romcom-blue/10">

      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="fixed top-24 left-4 z-10 bg-[#faf8f6]/90 backdrop-blur-sm p-3 rounded-full shadow-lg hover:scale-105 transition-transform"
      >
        <ChevronLeft className="w-6 h-6" />
      </button>

      {/* Backdrop */}
      {movie.backdropUrl && (
        <div className="absolute top-0 left-0 right-0 h-96 overflow-hidden -z-10">
          <img
            src={movie.backdropUrl}
            alt=""
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#faf8f6]/90" />
        </div>
      )}

      <div className="container mx-auto px-4 pt-24 pb-16">

        {/* ── MOVIE HEADER ── */}
        <div className="flex flex-col lg:flex-row gap-8 mb-12">

          {/* Poster */}
          <div className="lg:w-1/3">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="sticky top-24"
            >
              <div className="aspect-[2/3] rounded-2xl overflow-hidden shadow-2xl">
                {movie.posterUrl ? (
                  <img
                    src={movie.posterUrl}
                    alt={movie.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-romcom-purple to-romcom-blue flex items-center justify-center">
                    <div className="text-center p-8">
                      <Film className="w-16 h-16 text-white mx-auto mb-4" />
                      <h2 className="text-2xl font-bold text-white">{movie.title}</h2>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>

          {/* Info */}
          <div className="lg:w-2/3">
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>

              {/* Title + actions */}
              <div className="mb-4 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex-1 pr-0 lg:pr-4">
                    <h1 className="text-4xl lg:text-5xl font-playfair font-bold text-gray-800 mb-1">
                      {movie.title}
                    </h1>
                    {movie.releaseYear && (
                      <p className="text-xl text-gray-500">({movie.releaseYear})</p>
                    )}
                    {movie.tagline && (
                      <p className="text-lg italic text-gray-600 mt-2">"{movie.tagline}"</p>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 lg:gap-3 flex-shrink-0">
                    <button
                      onClick={() => toggleLike(movieId)}
                      className={`p-3 rounded-full shadow-lg hover:scale-105 transition-all ${
                        isLiked ? 'bg-romcom-red text-white' : 'bg-[#faf8f6] text-gray-700'
                      }`}
                      title={isLiked ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      <Heart className={`w-6 h-6 ${isLiked ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      onClick={() => toggleWatchlist(movieId)}
                      className={`p-3 rounded-full shadow-lg hover:scale-105 transition-all ${
                        isWatchlisted ? 'bg-yellow-400 text-white' : 'bg-[#faf8f6] text-gray-700'
                      }`}
                      title={isWatchlisted ? 'Remove from watchlist' : 'Add to watchlist'}
                    >
                      <Bookmark className={`w-6 h-6 ${isWatchlisted ? 'fill-current' : ''}`} />
                    </button>
                    <button
                      onClick={() => toggleWatched(movieId)}
                      className={`px-4 py-3 rounded-full shadow-lg hover:scale-105 transition-all text-sm font-medium ${
                        isWatched ? 'bg-green-500 text-white' : 'bg-[#faf8f6] text-gray-700'
                      }`}
                      title={isWatched ? 'Remove from Memory Lane' : 'Add to Memory Lane'}
                    >
                      {isWatched ? 'In Memory Lane' : 'Mark Watched'}
                    </button>
                  </div>
                </div>

                {/* The Notebook */}
                <div className="rounded-2xl border border-romcom-pink/20 bg-[#faf8f6]/80 p-4 max-w-xl">
                  <p className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-romcom-red" />
                    The Notebook — log a watch
                  </p>
                  <p className="text-xs text-gray-500 mb-3">
                    Choose the day you watched it. You can log the same movie again for a rewatch with another date.
                  </p>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date watched</label>
                  <input
                    type="date"
                    value={notebookWatchedOn}
                    onChange={e => {
                      setNotebookWatchedOn(e.target.value);
                      setNotebookError('');
                    }}
                    max={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`}
                    className="w-full text-sm rounded-xl border border-gray-200 px-3 py-2 mb-3 focus:ring-2 focus:ring-romcom-red/30 focus:border-romcom-pink outline-none"
                  />
                  <label className="block text-xs font-medium text-gray-600 mb-1">Optional note</label>
                  <textarea
                    value={notebookNote}
                    onChange={e => setNotebookNote(e.target.value)}
                    placeholder="Add a note...It's a NOTEBOOK."
                    rows={2}
                    className="w-full text-sm rounded-xl border border-gray-200 px-3 py-2 mb-2 focus:ring-2 focus:ring-romcom-red/30 focus:border-romcom-pink outline-none resize-none"
                  />
                  {notebookError && (
                    <p className="text-sm text-red-600 mb-2">{notebookError}</p>
                  )}
                  {notebookSaved && (
                    <p className="text-sm text-green-700 mb-2">Saved to The Notebook — check your profile.</p>
                  )}
                  <button
                    type="button"
                    onClick={handleLogNotebook}
                    disabled={isLoggingNotebook}
                    className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-gradient-to-r from-romcom-pink/90 to-romcom-red text-white shadow-md hover:opacity-95 transition-opacity disabled:opacity-60"
                  >
                    {isLoggingNotebook ? (
                      <Loader className="w-4 h-4 animate-spin" />
                    ) : (
                      <BookOpen className="w-4 h-4" />
                    )}
                    Add to The Notebook
                  </button>
                </div>
              </div>

              {/* Rating + stats */}
              <div className="flex flex-wrap gap-6 mb-6">
                <div className="flex items-center gap-3">
                  <StarRating rating={Math.round(rating)} />
                  <div>
                    <span className="font-bold text-2xl">{rating.toFixed(1)}</span>
                    <span className="text-gray-500 text-sm ml-1">/ 5</span>
                  </div>
                  {movie.voteCount && (
                    <span className="text-gray-500 text-sm">
                      ({movie.voteCount.toLocaleString()} ratings)
                    </span>
                  )}
                </div>
                {movie.runtime && (
                  <div className="flex items-center gap-1 text-gray-600">
                    <span className="font-medium">{movie.runtime} min</span>
                  </div>
                )}
              </div>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-6">
                {movie.season && (
                   <span className="bg-romcom-blue/20 text-romcom-blue px-3 py-1 rounded-full text-sm capitalize">
                    {movie.season === 'winter' ? '❄️' :
                     movie.season === 'spring' ? '🌸' :
                     movie.season === 'summer' ? '☀️' : '🍂'} {movie.season}
                  </span>
                )}
                {movie.moods?.map(mood => (
                  <span key={mood} className="bg-romcom-pink/20 text-romcom-red px-3 py-1 rounded-full text-sm capitalize">
                    {mood.replace('_', ' ')}
                  </span>
                ))}
                {movie.tropes?.map(trope => (
                  <span key={trope} className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm">
                    {trope}
                  </span>
                ))}
                {movie.holiday && movie.holiday !== 'none' && (
                  <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm capitalize">
                    {movie.holiday.replace('_', ' ')}
                  </span>
                )}
              </div>

              {/* Overview */}
              {movie.overview && (
                <div className="mb-6">
                  <h3 className="text-xl font-bold mb-3">Synopsis</h3>
                  <p className="text-gray-700 text-lg leading-relaxed">{movie.overview}</p>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex flex-wrap gap-4">
                {movie.trailerUrl && (
                  <a
                    href={movie.trailerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-primary flex items-center gap-2"
                  >
                    <Play className="w-5 h-5" />
                    Watch Trailer
                  </a>
                )}
                <button
                  onClick={() => {
                    if (!state.currentUser) { openLoginModal(); return; }
                    setEditingReviewId(null);
                    setReviewContent('');
                    setReviewRating(5);
                    setShowReviewForm(true);
                    setTimeout(() => {
                      document.getElementById('review-form')?.scrollIntoView({ behavior: 'smooth' });
                    }, 100);
                  }}
                  className="btn-secondary flex items-center gap-2"
                >
                  <MessageSquare className="w-5 h-5" />
                  {userReview ? 'Edit Review' : 'Write Review'}
                </button>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ── REVIEW FORM ── */}
        <AnimatePresence>
          {showReviewForm && (
            <motion.div
              id="review-form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="card mb-12"
            >
              <h3 className="text-2xl font-bold mb-6">
                {editingReviewId ? 'Edit Your Review' : 'Write a Review'}
              </h3>

              {/* Star rating */}
              <div className="flex items-center gap-4 mb-6">
                <span className="font-medium text-gray-700">Your Rating:</span>
                <StarRating rating={reviewRating} onRate={setReviewRating} size="lg" />
                <span className="font-bold text-xl text-romcom-red">{reviewRating}/5</span>
              </div>

              {/* Text */}
              <textarea
                value={reviewContent}
                onChange={e => setReviewContent(e.target.value)}
                placeholder="What did you think of this movie? Share your thoughts..."
                className="w-full p-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-romcom-red focus:border-transparent resize-none"
                rows={5}
              />
              {/* Spoiler toggle */}
              <div className="flex items-center gap-2 mt-3 mb-4">
                <input
                  type="checkbox"
                  id="spoiler"
                  checked={containsSpoiler}
                  onChange={e => setContainsSpoiler(e.target.checked)}
                  className="w-4 h-4 accent-romcom-red"
                />
                <label htmlFor="spoiler" className="text-gray-600 text-sm flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4 text-yellow-500" />
                  This review contains spoilers
                </label>
              </div>

              {reviewError && (
                <p className="text-red-500 text-sm mb-4">{reviewError}</p>
              )}

              <div className="flex gap-4">
                <button
                  onClick={handleSubmitReview}
                  disabled={isSubmittingReview}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {isSubmittingReview ? (
                    <><Loader className="w-4 h-4 animate-spin" /> Submitting...</>
                  ) : (
                    editingReviewId ? 'Save Changes' : 'Submit Review'
                  )}
                </button>
                <button
                  onClick={() => { setShowReviewForm(false); setEditingReviewId(null); setReviewError(''); }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── REVIEWS SECTION ── */}
        <div className="card mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-3xl font-playfair font-bold">
              Community Reviews
            </h2>
            <span className="text-gray-500">{reviewsTotal} reviews</span>
          </div>

          {/* ── SHIP RATING — above reviews ── */}
          <ShipRating
            movieTitle={movie.title}
            userRating={shipRating}
            onRate={handleShipRate}
            communityAvg={communityShipAvg}
            totalRatings={totalShipRatings}
          />

          {isLoadingReviews ? (
            <div className="flex justify-center py-8">
              <Loader className="w-8 h-8 text-romcom-red animate-spin" />
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">No reviews yet.</p>
              <p className="text-gray-400">Be the first to share your thoughts!</p>
              <button
                onClick={() => {
                  if (!state.currentUser) { openLoginModal(); return; }
                  setShowReviewForm(true);
                }}
                className="btn-primary mt-6"
              >
                Write the First Review
              </button>
            </div>
          ) : (
            <div className="space-y-8">
              {reviews.map(review => (
                <div key={review.id} className="border-b border-gray-100 pb-8 last:border-0">

                  {/* Review header */}
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-romcom-pink to-romcom-red flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden">
                        {review.user.avatar ? (
                          <img src={review.user.avatar} alt="" className="w-full h-full object-cover" />
                        ) : (
                          review.user.username.charAt(0).toUpperCase()
                        )}
                      </div>
                      <div>
                        <UserHoverCard user={review.user} navigate={navigate}>
                          <p
                            className="font-bold text-gray-800 cursor-pointer hover:text-romcom-red transition-colors"
                            onClick={() => navigate(`/users/${review.user.username}`)}
                          >
                            {review.user.name || review.user.username}
                          </p>
                        </UserHoverCard>
                        <div className="flex items-center gap-2">
                          <StarRating rating={review.rating} />
                          <span className="text-gray-500 text-xs">
                            {new Date(review.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric', month: 'short', day: 'numeric'
                            })}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Edit/delete for own review */}
                    {String(state.currentUser?.id) === review.user.id && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditReview(review)}
                          className="p-2 text-gray-400 hover:text-romcom-red transition-colors"
                          title="Edit review"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteReview(review.id)}
                          className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                          title="Delete review"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Spoiler warning */}
                  {review.containsSpoiler && (
                    <div className="flex items-center gap-2 bg-yellow-50 text-yellow-700 px-3 py-2 rounded-lg text-sm mb-3">
                      <AlertTriangle className="w-4 h-4" />
                      This review contains spoilers
                    </div>
                  )}

                  {/* Review content */}
                  <p className="text-gray-700 leading-relaxed mb-4">{review.content}</p>

                  {/* Like + comment buttons */}
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => handleLikeReview(review.id)}
                      className={`flex items-center gap-1.5 text-sm transition-colors ${
                        likedReviews.has(review.id)
                          ? 'text-romcom-red'
                          : 'text-gray-500 hover:text-romcom-red'
                      }`}
                    >
                      <ThumbsUp className={`w-4 h-4 ${likedReviews.has(review.id) ? 'fill-current' : ''}`} />
                      <span>{review.likesCount}</span>
                    </button>
                    <button
                      onClick={() => setExpandedComments(prev => ({
                        ...prev, [review.id]: !prev[review.id]
                      }))}
                      className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-romcom-blue transition-colors"
                    >
                      <MessageSquare className="w-4 h-4" />
                      <span>{review.commentsCount} {review.commentsCount === 1 ? 'comment' : 'comments'}</span>
                    </button>
                  </div>

                  {/* Comments section */}
                  <AnimatePresence>
                    {expandedComments[review.id] && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 pl-4 border-l-2 border-romcom-pink/30"
                      >
                        {/* Existing comments */}
                        <div className="space-y-3 mb-4">
                          {review.comments.map(comment => (
                            <div key={comment.id} className="flex gap-3">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-romcom-blue to-romcom-purple flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {comment.user.username.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 bg-romcom-pink/5 rounded-xl px-4 py-3">
                                <div className="flex items-center justify-between mb-1">
                                  <span className="font-medium text-sm text-gray-800">
                                    {comment.user.name || comment.user.username}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="text-xs text-gray-400">
                                      {new Date(comment.createdAt).toLocaleDateString()}
                                    </span>
                                    {String(state.currentUser?.id) === comment.user.id && (
                                      <button
                                        onClick={() => handleDeleteComment(review.id, comment.id)}
                                        className="text-gray-300 hover:text-red-400 transition-colors"
                                      >
                                        <Trash2 className="w-3 h-3" />
                                      </button>
                                    )}
                                  </div>
                                </div>
                                <p className="text-gray-700 text-sm">{comment.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Add comment input */}
                        {state.currentUser && (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              value={commentInputs[review.id] || ''}
                              onChange={e => setCommentInputs(prev => ({
                                ...prev, [review.id]: e.target.value
                              }))}
                              onKeyDown={e => e.key === 'Enter' && handleSubmitComment(review.id)}
                              placeholder="Add a comment..."
                              className="flex-1 px-4 py-2 text-sm border border-gray-200 rounded-full focus:ring-2 focus:ring-romcom-red focus:border-transparent focus:outline-none"
                            />
                            <button
                              onClick={() => handleSubmitComment(review.id)}
                              disabled={submittingComment === review.id}
                              className="p-2 bg-romcom-red text-white rounded-full hover:bg-red-600 transition-colors disabled:opacity-50"
                            >
                              {submittingComment === review.id ? (
                                <Loader className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── SIMILAR MOVIES ── */}
        {similarMovies.length > 0 && (
          <div>
            <h2 className="text-3xl font-playfair font-bold text-gray-800 mb-8">
              You Might Also Like
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {similarMovies.map(similar => (
                <motion.div
                  key={similar.id}
                  whileHover={{ y: -4 }}
                  onClick={() => navigate(`/movies/${similar.id}`)}
                  className="cursor-pointer"
                >
                  <div className="aspect-[2/3] rounded-xl overflow-hidden shadow-md mb-2">
                    {similar.posterUrl ? (
                      <img
                        src={similar.posterUrl}
                        alt={similar.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-romcom-mauve to-romcom-blue flex items-center justify-center p-2">
                        <p className="text-white text-xs font-bold text-center">{similar.title}</p>
                      </div>
                    )}
                  </div>
                  <p className="text-sm font-medium text-gray-800 line-clamp-2">{similar.title}</p>
                  {similar.releaseYear && (
                    <p className="text-xs text-gray-500">{similar.releaseYear}</p>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default MovieDetailPage;